import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  PlayIcon,
  TrashIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  Squares2X2Icon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowsPointingOutIcon,
} from '@heroicons/react/24/outline';
import monitorsService from '../../../services/monitorsService';
import { useMonitorValues, useRunMonitor, useDeleteMonitor } from '../../../hooks/useMonitors';
import MonitorSparkline from './MonitorSparkline';
import MonitorHistoryModal from './MonitorHistoryModal';
import MonitorDetailModal from './MonitorDetailModal';

// Single monitor card used in the Monitors page grid. Shows the latest
// value, sparkline, threshold/method badge, and per-card actions:
// Run Now / View History / Edit / Delete.
//
// For segmented monitors:
//   - Collapsed: shows segment count badge + aggregate latest (the most
//     recent value across all segments — a quick "any movement" signal,
//     not a sum/avg).
//   - Expanded: per-segment rows with name, latest value, mini-sparkline.
//     Triggered by the "Show segments" toggle, fetches more rows so
//     each segment has trend data to draw.
//
// The engine doesn't persist an aggregate row by design (decision in
// MONITORS_REFACTOR.md Step 1); aggregates would just be stale duplicates
// of monitor_values data.
const MonitorCard = ({ monitor, connectionId, onEdit }) => {
  const [historyOpen, setHistoryOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Track the confirm-delete revert timer so we can clear it on unmount.
  // Without this, deleting (which unmounts the card via list refetch)
  // leaves a stale setTimeout that fires setConfirmDelete on a torn-down
  // component — React 18 silently no-ops it but it's still incorrect.
  const confirmTimerRef = useRef(null);
  useEffect(() => () => {
    if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
  }, []);

  // A monitor is "segmented" if EITHER monitor.segment_by is set (the
  // canonical signal, set automatically by the form for built-in
  // metrics and now also for custom_sql with a segment_column) OR the
  // source_config has a segment_column (legacy custom_sql monitors
  // created before the 2026-05-19 form fix that didn't auto-mirror).
  // Without the OR branch, older custom_sql monitors would render with
  // segment_by=null and the trend line would zig-zag across per-
  // segment points instead of showing one trend point per run.
  const isSegmented =
    !!monitor.segment_by ||
    (monitor.source_type === 'custom_sql' &&
      !!monitor.source_config?.segment_column);

  // Pull last few readings for the aggregate sparkline. Limit kept small —
  // sparkline only needs trend shape, not a long tail.
  const { data: values = [] } = useMonitorValues(connectionId, monitor.id, {
    limit: 50,
  });

  // Separate, larger fetch for the expanded per-segment view. Only runs
  // when the user clicks "Show segments" so we don't pay the cost for
  // collapsed cards. Both queries share the same invalidation key, so
  // Run Now refreshes both automatically.
  //
  // 500 rows = roughly 20 history points per segment if a monitor has
  // 25 segments; enough for a meaningful mini-sparkline. Monitors with
  // more than ~50 segments will run out of history per segment — that's
  // a future iteration (paginate or fetch per-segment on demand).
  const {
    data: expandedValues = [],
    isLoading: expandedLoading,
  } = useMonitorValues(
    connectionId,
    monitor.id,
    { limit: 500, enabled: isSegmented && expanded }
  );

  const runMutation = useRunMonitor(connectionId, monitor.id);
  const deleteMutation = useDeleteMonitor(connectionId);

  // The user's preferred aggregation method for collapsing per-segment
  // values into an overall number. Lives in source_config because it's
  // a custom_sql concept; for built-in metrics we use the engine
  // aggregate and ignore this. Defaults to 'sum' for older monitors
  // that predate the field.
  const aggregationMethod =
    monitor.source_config?.aggregation_method || 'sum';

  // Collapsed-card aggregate trend. Three cases, in priority order:
  //
  // 1. Scalar monitor: nothing to aggregate — just use raw rows.
  // 2. Segmented monitor where the engine has persisted aggregate rows
  //    (segment_value=NULL): use those directly. They're the real
  //    un-grouped metric (e.g., COUNT(*) FROM table = 1.5M total), not
  //    an approximation. Engine started populating these on
  //    2026-05-19; runs after that will have correct totals.
  // 3. Segmented monitor with no aggregate rows yet (older built-in
  //    monitor not yet re-run, or a custom_sql segmented monitor which
  //    doesn't get an engine-computed aggregate): fall back to
  //    client-side aggregation using the user's chosen method.
  const aggregatedValues = useMemo(() => {
    if (!isSegmented) return values;
    const nullSegmentRows = values.filter(
      (v) => v.segment_value === null || v.segment_value === undefined,
    );
    if (nullSegmentRows.length > 0) return nullSegmentRows;
    return monitorsService.aggregateValuesByTimestamp(values, aggregationMethod);
  }, [isSegmented, values, aggregationMethod]);

  // Latest aggregate reading. For segmented monitors that's the
  // engine's un-grouped total when available, or avg-across-segments as
  // a fallback. For scalar it's just the latest value.
  const latest = aggregatedValues[0];
  const latestValue = latest?.value;
  const lastCollectedAt = monitor.last_collected_at || latest?.calculated_at;
  // True when we're showing a fallback client-side aggregate rather
  // than the engine's un-grouped query result. Drives the label below
  // so the user knows what the number actually represents.
  const usingClientAggregate =
    isSegmented && latest && latest._aggregated === true;

  // Segment count from the loaded values. Capped at whatever fits in
  // the limit so the badge can read "?" for huge segment counts the
  // collapsed fetch didn't see fully. The expanded fetch gives a more
  // accurate count when open.
  const distinctSegments = useMemo(() => {
    if (!isSegmented) return 0;
    const source = expanded ? expandedValues : values;
    return new Set(
      source.map((v) => v.segment_value).filter((s) => s !== null && s !== undefined)
    ).size;
  }, [isSegmented, expanded, values, expandedValues]);

  // Build per-segment rows for the expanded view. Group by segment_value,
  // sort alphabetically (predictable; recency-sort would shuffle the
  // list every refresh which is jarring). Backend returns DESC by
  // calculated_at so `rows[0]` is the latest reading for each segment.
  const segmentRows = useMemo(() => {
    if (!isSegmented || !expanded) return [];
    const grouped = new Map();
    for (const v of expandedValues) {
      const seg = v.segment_value;
      if (seg === null || seg === undefined) continue;
      if (!grouped.has(seg)) grouped.set(seg, []);
      grouped.get(seg).push(v);
    }
    return [...grouped.entries()]
      .sort(([a], [b]) => String(a).localeCompare(String(b)))
      .map(([segment, rows]) => ({
        segment,
        latest: rows[0],
        // Sparkline expects ascending — reverse here so each segment's
        // mini-trend reads left-to-right oldest-to-newest like the main one.
        history: rows,
      }));
  }, [isSegmented, expanded, expandedValues]);

  // Evaluation-mode summary line — keeps the card body tight without
  // forcing users to open the edit form to see what kind of detection
  // is running.
  const evalSummary = () => {
    const cfg = monitor.evaluation_config || {};
    if (monitor.evaluation_mode === 'statistical') {
      const method = cfg.method || 'zscore';
      const sens = cfg.sensitivity != null ? cfg.sensitivity : 1.0;
      return `${method}, sensitivity ${Number(sens).toFixed(1)}`;
    }
    if (monitor.evaluation_mode === 'threshold') {
      const dir = cfg.direction || 'below';
      const parts = [];
      if (cfg.warning != null) parts.push(`warn ${cfg.warning}`);
      if (cfg.critical != null) parts.push(`crit ${cfg.critical}`);
      return `${dir}: ${parts.join(', ') || '(no thresholds)'}`;
    }
    return monitor.evaluation_mode;
  };

  // Source-type summary line.
  const sourceSummary = () => {
    const cfg = monitor.source_config || {};
    if (monitor.source_type === 'builtin_table_metric') {
      return `${cfg.metric} on ${cfg.table_name || '(table)'}`;
    }
    if (monitor.source_type === 'builtin_column_metric') {
      return `${cfg.metric} on ${cfg.table_name || '(table)'}.${cfg.column_name || '(col)'}`;
    }
    if (monitor.source_type === 'custom_sql') {
      return `Custom SQL (value=${cfg.value_column}${cfg.segment_column ? `, segment=${cfg.segment_column}` : ''})`;
    }
    return monitor.source_type;
  };

  const handleDelete = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      // Auto-revert after 4s. Stash the timer id so the unmount effect
      // can clear it if the delete completes (and unmounts us) first.
      if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
      confirmTimerRef.current = setTimeout(() => {
        setConfirmDelete(false);
        confirmTimerRef.current = null;
      }, 4000);
      return;
    }
    await deleteMutation.mutateAsync(monitor.id);
  };

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
      <div className="p-5">
        {/* Header: name + active badge + segment badge */}
        <div className="flex items-start justify-between">
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-medium text-gray-900 truncate">
              {monitor.display_name || monitor.name}
            </h3>
            <p className="mt-0.5 text-xs text-gray-500 truncate">
              {sourceSummary()}
            </p>
          </div>
          <div className="ml-2 flex flex-col items-end gap-1">
            {/* Expand-to-modal button. Top-right of the card per the
                request — the card surface truncates long names and
                source-config text; the modal shows everything uncut. */}
            <button
              type="button"
              onClick={() => setDetailOpen(true)}
              className="p-1 text-gray-400 hover:text-gray-600 rounded"
              aria-label="Expand details"
              title="Expand details"
            >
              <ArrowsPointingOutIcon className="h-4 w-4" />
            </button>
            {/* Three-state status badge. Pre-2026-05-21 this only showed
                Active vs Inactive based on is_active, which conflated
                "scheduled and firing" with "manual-only". A user who
                unchecked "Run automatically on a schedule" in the form
                still saw "Active" even though the monitor only fires on
                Run Now (BUGS.md: ST3). The Manual state surfaces that
                distinction. */}
            {!monitor.is_active ? (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                Inactive
              </span>
            ) : monitor.schedule_type === 'manual' ? (
              <span
                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700"
                title="This monitor only runs when you click Run Now — no automatic schedule"
              >
                <ClockIcon className="h-3 w-3 mr-1" />
                Manual
              </span>
            ) : (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                Active
              </span>
            )}
            {isSegmented && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                <Squares2X2Icon className="h-3 w-3 mr-1" />
                {distinctSegments || '?'} segments
              </span>
            )}
          </div>
        </div>

        {/* Latest value. When the segmented view is expanded, the
            aggregate number is misleading (it's just the latest row,
            not a sum/avg across segments), so we hide it and direct
            attention to the per-segment list below instead. */}
        {!(isSegmented && expanded) && (
          <div className="mt-3">
            {/* min-w-0 + truncate at the parent ensures the value
                respects the card width even if formatCompact's output
                grows (e.g., negative trillions). title= exposes the
                full unabbreviated number on hover. */}
            <div className="flex items-baseline min-w-0">
              <span
                className="text-3xl font-semibold text-gray-900 truncate"
                title={monitorsService.formatValue(latestValue)}
              >
                {monitorsService.formatCompact(latestValue)}
              </span>
              {isSegmented && (
                <span className="ml-2 text-xs text-gray-500 flex-shrink-0">
                  {usingClientAggregate
                    ? `${aggregationMethod} across segments`
                    : 'overall'}
                </span>
              )}
            </div>
            {lastCollectedAt && (
              <p className="mt-1 text-xs text-gray-500 flex items-center">
                <ClockIcon className="h-3 w-3 mr-1" />
                {new Date(lastCollectedAt).toLocaleString()}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-400">{evalSummary()}</p>
            {/* Schedule summary — small line under eval summary so the
                user can spot at a glance whether the monitor is
                manual-only, interval-driven, or on a daily/weekly cron.
                Mirrors what the form configured. */}
            <p className="mt-0.5 text-xs text-gray-400">
              {monitorsService.scheduleSummary(monitor)}
            </p>
          </div>
        )}

        {/* Aggregate sparkline — same rule: hide when expanded so the
            per-segment view is the focus. Uses aggregatedValues so
            segmented monitors show one trend point per run, not one
            per (segment × run). */}
        {!(isSegmented && expanded) && (
          <div className="mt-3">
            <MonitorSparkline values={aggregatedValues} />
          </div>
        )}

        {/* Expand toggle for segmented monitors. Placed inside the body
            (not the actions footer) so its proximity to the segment badge
            reads as "this badge has detail; click to see it". */}
        {isSegmented && (
          <div className={`${isSegmented && expanded ? '' : 'mt-3'}`}>
            <button
              type="button"
              onClick={() => setExpanded((prev) => !prev)}
              className="text-xs font-medium text-indigo-600 hover:text-indigo-800 inline-flex items-center"
            >
              {expanded ? (
                <>
                  <ChevronUpIcon className="h-3 w-3 mr-1" />
                  Hide segments
                </>
              ) : (
                <>
                  <ChevronDownIcon className="h-3 w-3 mr-1" />
                  Show segments
                </>
              )}
            </button>
          </div>
        )}

        {/* Per-segment list, only rendered when expanded. Scrollable so
            a 25-segment monitor doesn't push the card to an unusable
            height; max-h chosen to show ~6 rows comfortably. */}
        {isSegmented && expanded && (
          <div className="mt-3 border-t border-gray-100 pt-3 max-h-72 overflow-y-auto">
            <p className="text-xs font-medium text-gray-700 mb-2">
              Segments ({distinctSegments})
              {lastCollectedAt && (
                <span className="ml-2 font-normal text-gray-500">
                  as of {new Date(lastCollectedAt).toLocaleString()}
                </span>
              )}
            </p>
            {segmentRows.length === 0 ? (
              expandedLoading ? (
                <p className="text-xs text-gray-500 py-2 inline-flex items-center">
                  <ArrowPathIcon className="h-3 w-3 mr-1 animate-spin" />
                  Loading segments...
                </p>
              ) : (
                <p className="text-xs text-gray-500 py-2">
                  No segment data yet. Run the monitor to populate.
                </p>
              )
            ) : (
              <ul className="divide-y divide-gray-100">
                {segmentRows.map(({ segment, latest: segLatest, history }) => (
                  <li
                    key={segment}
                    className="flex items-center py-1.5 text-sm"
                  >
                    <span className="flex-1 truncate text-gray-900" title={segment}>
                      {segment}
                    </span>
                    <span
                      className="mx-3 font-mono text-gray-700 text-xs"
                      title={monitorsService.formatValue(segLatest?.value)}
                    >
                      {monitorsService.formatCompact(segLatest?.value)}
                    </span>
                    <div className="w-20 h-7">
                      <MonitorSparkline values={history} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="bg-gray-50 px-5 py-3 flex items-center justify-between">
        <button
          onClick={() => runMutation.mutate()}
          disabled={runMutation.isPending}
          className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
        >
          {runMutation.isPending ? (
            <ArrowPathIcon className="h-4 w-4 mr-1 animate-spin" />
          ) : (
            <PlayIcon className="h-4 w-4 mr-1" />
          )}
          Run Now
        </button>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setHistoryOpen(true)}
            className="text-sm text-indigo-600 hover:text-indigo-800"
          >
            View History
          </button>
          <button
            onClick={() => onEdit && onEdit(monitor)}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Edit
          </button>
          <button
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            className={`text-sm disabled:opacity-50 ${
              confirmDelete
                ? 'text-red-700 font-medium'
                : 'text-red-500 hover:text-red-700'
            }`}
            title={confirmDelete ? 'Click again to confirm' : 'Delete monitor'}
          >
            {confirmDelete ? (
              <span className="inline-flex items-center">
                <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                Confirm
              </span>
            ) : (
              <TrashIcon className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>

      <MonitorHistoryModal
        isOpen={historyOpen}
        onClose={() => setHistoryOpen(false)}
        monitor={monitor}
        connectionId={connectionId}
      />

      <MonitorDetailModal
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        monitor={monitor}
        connectionId={connectionId}
        aggregatedValues={aggregatedValues}
        onEdit={onEdit}
      />
    </div>
  );
};

export default MonitorCard;
